import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

function Breadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();

  const getClassroomDashboardPath = (classroomId, tab = 'assignments') =>
    `/admin/classrooms/classroom-dashboard/${classroomId}?active-tab=${tab}`;

  const getReturnTabFromSearch = (fallback = 'assignments') => {
    const tab = new URLSearchParams(location.search).get('active-tab');
    return tab || fallback;
  };

  // Show breadcrumbs for all admin paths
  if (!location.pathname.startsWith('/admin/')) {
    return null;
  }

  // Filter out empty strings and 'admin'
  const pathnames = location.pathname
    .split('/')
    .filter((x) => x !== '' && x !== 'admin');

  const handleBack = () => {
    navigate(-1);
  };

  // Create a mapping of path segments to readable names and icons
  const pathMap = {
    'dashboard': {
      name: 'Dashboard',
      icon: 'fluent:home-24-regular'
    },
    'feed': {
      name: 'Feed',
      icon: 'fluent:news-24-regular'
    },
    'my-feed': {
      name: 'My Feed',
      icon: 'fluent:news-24-regular'
    },
    'roles-and-access': {
      name: 'Roles & Access',
      icon: 'fluent:shield-lock-24-regular'
    },
    'courses': {
      name: 'All Courses',
      icon: 'fluent:book-24-regular'
    },
    'edit-create': {
      name: 'Edit Course Details',
      icon: 'fluent:edit-24-regular'
    },
    'classrooms': {
      name: 'Classrooms',
      icon: 'fluent:video-person-24-regular'
    },
    'classroom-dashboard': {
      name: 'Classroom Dashboard',
      icon: 'fluent:video-person-24-regular'
    },
    'trainees': {
      name: 'Trainees',
      icon: 'fluent:people-24-regular'
    },
    'assessment': {
      name: 'Assessment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'ClassroomDashboard': {
      name: 'Classroom Dashboard',
      icon: 'fluent:video-person-24-regular'
    },
    'ClassroomAssessmentDetails': {
      name: 'Assessment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'assingment': {
      name: 'Assignment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'ClassroomAssignmentDetails': {
      name: 'Assignment',
      icon: 'fluent:clipboard-task-list-24-regular'
    },
    'assignment-quiz': {
      name: 'Assignment Quiz',
      icon: 'fluent:quiz-new-24-regular'
    },
    'analytics': {
      name: 'Course Analytics',
      icon: 'fluent:data-trending-24-regular'
    },
    'module': {
      name: 'Course Modules',
      icon: 'fluent:book-information-24-regular'
    },
    'content': {
      name: 'Content',
      icon: 'fluent:document-24-regular'
    },
    'video': {
      name: 'Edit Video',
      icon: 'fluent:video-24-regular'
    },
    'quiz': {
      name: 'Edit Assessment',
      icon: 'fluent:clipboard-task-list-ltr-24-regular'
    },
    'survey': {
      name: 'Edit Survey',
      icon: 'fluent:form-24-regular'
    },
    'document': {
      name: 'Edit Document',
      icon: 'fluent:document-24-regular'
    }
  };

  const getPathInfo = (path) => {
    // Handle encoded IDs
    if (path.includes('%')) {
      const pathType = pathnames[pathnames.indexOf(path) - 1];
      switch (pathType) {
        case 'ClassroomDashboard':
          return {
            name: 'Classroom Dashboard',
            icon: 'fluent:video-person-24-regular'
          };
        case 'assessment':
          return {
            name: 'Assessment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'ClassroomAssessmentDetails':
          return {
            name: 'Assessment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'assingment':
          return {
            name: 'Assignment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'ClassroomAssignmentDetails':
          return {
            name: 'Assignment',
            icon: 'fluent:clipboard-task-list-24-regular'
          };
        case 'video':
          return {
            name: 'Edit Video',
            icon: 'fluent:video-24-regular'
          };
        case 'quiz':
          return {
            name: 'Edit Assessment',
            icon: 'fluent:clipboard-task-list-ltr-24-regular'
          };
        case 'survey':
          return {
            name: 'Edit Survey',
            icon: 'fluent:form-24-regular'
          };
        case 'document':
          return {
            name: 'Edit Document',
            icon: 'fluent:document-24-regular'
          };
        case 'content':
          return {
            name: 'Content',
            icon: 'fluent:document-24-regular'
          };
        default:
          return {
            name: 'Module Details',
            icon: 'fluent:book-information-24-regular'
          };
      }
    }

    return pathMap[path] || {
      name: path.charAt(0).toUpperCase() + path.slice(1),
      icon: 'fluent:document-24-regular'
    };
  };

  // Get the current page title
  const getPageTitle = () => {
    if (pathnames.length === 0) return 'Dashboard';

    if (pathnames.includes('roles-and-access')) {
      return 'Roles & Access';
    }

    if (pathnames.includes('traineesAnalytics')) {
      return 'Trainee Analytics';
    }

    if (pathnames.includes('CourseAnalyticsAssessmentDetails')) {
      return 'Assessment Analytics';
    }

    if (pathnames.includes('CourseAnalyticsSurveyDetails')) {
      return 'Survey Analytics';
    }

    if (pathnames.includes('edit-create')) {
      return 'Edit Course Details';
    }

    if (pathnames.includes('classrooms') && pathnames.length === 1) {
      return 'Classrooms';
    }

    if (pathnames.includes('classroom-dashboard') && pathnames.length === 3) {
      return 'Classroom Dashboard';
    }

    if (pathnames.includes('ClassroomAssignmentDetails')) {
      return 'Assignment';
    }

    if (pathnames.includes('ClassroomAssessmentDetails')) {
      return 'Assessment';
    }

    if (pathnames.includes('classroom-dashboard') && pathnames.includes('trainees')) {
      return 'Trainees';
    }

    const lastSegment = pathnames[pathnames.length - 2]; // Get the type (video, quiz, etc.)
    const pathInfo = getPathInfo(pathnames[pathnames.length - 1]);

    switch (lastSegment) {
      case 'analytics':
        return 'Course Analytics';
      case 'video':
        return 'Edit Video';
      case 'quiz':
        return 'Edit Assessment';
      case 'survey':
        return 'Edit Survey';
      case 'document':
        return 'Edit Document';
      case 'content':
        return 'Content';
      case 'module':
        return 'Course Modules';
      default:
        return pathInfo.name;
    }
  };

  // Build the breadcrumb path
  const buildPath = (index) => {
    return `/admin/${pathnames.slice(0, index + 1).join('/')}`;
  };

  // Custom breadcrumb items for specific pages
  const getBreadcrumbItems = () => {
    if (pathnames.length === 1 && pathnames[0] === 'dashboard') {
      return [{
        name: 'Dashboard',
        icon: 'fluent:home-24-regular',
        path: '/admin/dashboard'
      }];
    }

    // Add new condition for roles and access
    if (pathnames.includes('roles-and-access')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Roles & Access',
          icon: 'fluent:shield-lock-24-regular',
          path: location.pathname
        }
      ];
    }

    // Handle trainee analytics path
    if (pathnames.includes('traineesAnalytics')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Trainees',
          icon: 'fluent:people-24-regular',
          path: '/admin/trainees'
        },
        {
          name: 'Trainee Analytics',
          icon: 'fluent:data-trending-24-regular',
          path: location.pathname
        }
      ];
    }

    // Handle ClassroomDashboard assessment path
    if (pathnames.includes('ClassroomAssessmentDetails')) {
      const classroomId = pathnames[pathnames.indexOf('classroom-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: getClassroomDashboardPath(classroomId, 'assessments')
        },
        {
          name: 'Assessment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    // Handle ClassroomDashboard assignment path
    if (pathnames.includes('ClassroomAssignmentDetails')) {
      const classroomId = pathnames[pathnames.indexOf('classroom-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: getClassroomDashboardPath(classroomId, 'assignments')
        },
        {
          name: 'Assignment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    // Handle assignment-quiz path
    if (pathnames.includes('assignment-quiz')) {
      const assignmentQuizIndex = pathnames.indexOf('assignment-quiz');
      const classroomId = pathnames[assignmentQuizIndex + 1];

      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classrooms',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/classrooms'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: getClassroomDashboardPath(classroomId, getReturnTabFromSearch('assignments'))
        },
        {
          name: 'Quiz Assignment',
          icon: 'fluent:quiz-new-24-regular',
          path: location.pathname
        }
      ];
    }

    if (pathnames.includes('analytics')) {
      const hasAssessmentDetails = pathnames.includes('CourseAnalyticsAssessmentDetails');
      const hasSurveyDetails = pathnames.includes('CourseAnalyticsSurveyDetails');

      if (hasAssessmentDetails) {
        return [
          {
            name: 'Dashboard',
            icon: 'fluent:home-24-regular',
            path: '/admin/dashboard'
          },
          {
            name: 'All Courses',
            icon: 'fluent:book-24-regular',
            path: '/admin/courses'
          },
          {
            name: 'Course Analytics',
            icon: 'fluent:data-trending-24-regular',
            path: `/admin/courses/analytics/${pathnames[pathnames.indexOf('analytics') + 1]}?tab=assessment`
          },
          {
            name: 'Assessment Analytics',
            icon: 'fluent:clipboard-task-list-24-regular',
            path: location.pathname
          }
        ];
      }

      if (hasSurveyDetails) {
        return [
          {
            name: 'Dashboard',
            icon: 'fluent:home-24-regular',
            path: '/admin/dashboard'
          },
          {
            name: 'All Courses',
            icon: 'fluent:book-24-regular',
            path: '/admin/courses'
          },
          {
            name: 'Course Analytics',
            icon: 'fluent:data-trending-24-regular',
            path: `/admin/courses/analytics/${pathnames[pathnames.indexOf('analytics') + 1]}?tab=survey`
          },
          {
            name: 'Survey Analytics',
            icon: 'fluent:form-24-regular',
            path: location.pathname
          }
        ];
      }

      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'All Courses',
          icon: 'fluent:book-24-regular',
          path: '/admin/courses'
        },
        {
          name: 'Course Analytics',
          icon: 'fluent:data-trending-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for classroom trainees
    if (pathnames.includes('classroom-dashboard') && pathnames.includes('trainees')) {
      const classroomId = pathnames[pathnames.indexOf('classroom-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classrooms',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/classrooms'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: getClassroomDashboardPath(classroomId, getReturnTabFromSearch('assignments'))
        },
        {
          name: 'Trainees',
          icon: 'fluent:people-24-regular',
          path: location.pathname + location.search
        }
      ];
    }

    // Add new condition for classroom assessment details
    if (pathnames.includes('ClassroomAssessmentDetails')) {
      const classroomId = pathnames[pathnames.indexOf('classroom-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classrooms',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/classrooms'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: getClassroomDashboardPath(classroomId, 'assessments')
        },
        {
          name: 'Assessment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for classroom assignment details
    if (pathnames.includes('ClassroomAssignmentDetails')) {
      const classroomId = pathnames[pathnames.indexOf('classroom-dashboard') + 1];
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classrooms',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/classrooms'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: getClassroomDashboardPath(classroomId, 'assignments')
        },
        {
          name: 'Assignment',
          icon: 'fluent:clipboard-task-list-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for classroom dashboard
    if (pathnames.includes('classroom-dashboard')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classrooms',
          icon: 'fluent:video-person-24-regular',
          path: '/admin/classrooms'
        },
        {
          name: 'Classroom Dashboard',
          icon: 'fluent:video-person-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for classrooms main page
    if (pathnames.includes('classrooms') && pathnames.length === 1) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'Classrooms',
          icon: 'fluent:video-person-24-regular',
          path: location.pathname
        }
      ];
    }

    // Add new condition for edit-create course
    if (pathnames.includes('edit-create')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'All Courses',
          icon: 'fluent:book-24-regular',
          path: '/admin/courses'
        },
        {
          name: 'Edit Course Details',
          icon: 'fluent:edit-24-regular',
          path: location.pathname
        }
      ];
    }

    if (pathnames.includes('module') && pathnames.includes('content')) {
      const moduleIndex = pathnames.indexOf('module');
      const contentIndex = pathnames.indexOf('content');
      const contentType = pathnames[contentIndex + 2]; // Get content type (video, quiz, etc.)
      const typeNames = {
        'video': 'Edit Video',
        'quiz': 'Edit Assessment',
        'survey': 'Edit Survey',
        'document': 'Edit Document'
      };
      const typeIcons = {
        'video': 'fluent:video-24-regular',
        'quiz': 'fluent:clipboard-task-list-ltr-24-regular',
        'survey': 'fluent:form-24-regular',
        'document': 'fluent:document-24-regular'
      };

      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'All Courses',
          icon: 'fluent:book-24-regular',
          path: '/admin/courses'
        },
        {
          name: 'Course Modules',
          icon: 'fluent:book-information-24-regular',
          path: `/admin/courses/module/${pathnames[moduleIndex + 1]}`
        },
        {
          name: 'Content',
          icon: 'fluent:document-24-regular',
          path: `/admin/courses/module/${pathnames[moduleIndex + 1]}/content/${pathnames[contentIndex + 1]}`
        },
        {
          name: typeNames[contentType],
          icon: typeIcons[contentType],
          path: location.pathname
        }
      ];
    } else if (pathnames.includes('module')) {
      return [
        {
          name: 'Dashboard',
          icon: 'fluent:home-24-regular',
          path: '/admin/dashboard'
        },
        {
          name: 'All Courses',
          icon: 'fluent:book-24-regular',
          path: '/admin/courses'
        },
        {
          name: 'Course Modules',
          icon: 'fluent:book-information-24-regular',
          path: location.pathname
        }
      ];
    }

    return null;
  };

  const customBreadcrumbs = getBreadcrumbItems();

  return (
    <nav aria-label="breadcrumb" className="tw-bg-white tw-py-4 tw-px-6 tw-mb-4 tw-rounded-xl tw-shadow-sm">
      <div className="tw-flex tw-justify-between tw-items-center">
        {/* Page Title with Back Button */}
        <div className="tw-flex tw-items-center">
          {pathnames.length > 1 && (
            <button
              onClick={handleBack}
              className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button btn-icon ui-button-light tw-rounded-full tw-me-2"}
              style={{
                width: '32px',
                height: '32px',
                padding: '0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #e0e0e0'
              }}
            >
              <Icon icon="fluent:arrow-left-24-regular" width="20" height="20" />
            </button>
          )}
          <h4 className="tw-mb-0 tw-font-bold tw-flex tw-items-center">
            <Icon
              icon={getPathInfo(pathnames[pathnames.length - 1] || 'dashboard').icon}
              className="tw-me-2"
              width="24"
              height="24"
            />
            {getPageTitle()}
          </h4>
        </div>

        {/* Breadcrumbs on the right */}
        <ol className="tw-m-0 tw-p-0 breadcrumb tw-mb-0 tw-items-center tw-hidden md:tw-flex">
          {customBreadcrumbs ? (
            // Render custom breadcrumbs for module and content pages
            customBreadcrumbs.map((item, index) => (
              <React.Fragment key={`${item.path}-${index}`}>
                {index > 0 && (
                  <li className="!tw-text-[var(--secondary-color)] tw-mx-1 tw-text-slate-500">
                    <Icon icon="fluent:chevron-right-24-regular" width="16" height="16" />
                  </li>
                )}
                <li className={`tw-flex tw-items-center breadcrumb-item   ${index === customBreadcrumbs.length - 1 ? "![font-weight:var(--font-weight-semibold)] active tw-font-semibold" : ""}`}>
                  {index === customBreadcrumbs.length - 1 ? (
                    <span className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-flex tw-items-center">
                      <Icon icon={item.icon} className="tw-me-1" width="18" height="18" />
                      {item.name}
                    </span>
                  ) : (
                    <Link
                      to={item.path}
                      className="!tw-text-[var(--secondary-color)] tw-no-underline tw-text-slate-500 tw-flex tw-items-center"
                    >
                      <Icon icon={item.icon} className="tw-me-1" width="18" height="18" />
                      {item.name}
                    </Link>
                  )}
                </li>
              </React.Fragment>
            ))
          ) : (
            // Default breadcrumb rendering
            <>
          <li className="tw-flex tw-items-center breadcrumb-item">
            <Link
              to="/admin/dashboard"
              className="!tw-text-[var(--secondary-color)] tw-no-underline tw-text-slate-500 tw-flex tw-items-center"
            >
              <Icon icon="fluent:home-24-regular" className="tw-me-1" width="18" height="18" />
                  <span>Dashboard</span>
            </Link>
          </li>

          {pathnames.map((value, index) => {
            const to = buildPath(index);
            const isLast = index === pathnames.length - 1;
            const pathInfo = getPathInfo(value);

            return (
              <React.Fragment key={`${to}-${index}`}>
                <li className="!tw-text-[var(--secondary-color)] tw-mx-1 tw-text-slate-500">
                  <Icon icon="fluent:chevron-right-24-regular" width="16" height="16" />
                </li>
                <li className={`tw-flex tw-items-center breadcrumb-item   ${isLast ? "![font-weight:var(--font-weight-semibold)] active tw-font-semibold" : ""}`}>
                {isLast ? (
                    <span className="!tw-text-[var(--primary-color)] tw-text-brand-600 tw-flex tw-items-center">
                      <Icon icon={pathInfo.icon} className="tw-me-1" width="18" height="18" />
                      {pathInfo.name}
                    </span>
                ) : (
                    <Link
                      to={to}
                      className="!tw-text-[var(--secondary-color)] tw-no-underline tw-text-slate-500 tw-flex tw-items-center"
                    >
                      <Icon icon={pathInfo.icon} className="tw-me-1" width="18" height="18" />
                      {pathInfo.name}
                    </Link>
                  )}
                  </li>
              </React.Fragment>
            );
          })}
            </>
          )}
        </ol>
      </div>
    </nav>
  );
}

export default Breadcrumbs;
